import {
    Box,
    Typography,
    Stack,
    Grid,
    useTheme,
    alpha,
    Divider,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';

interface HotkeyEntry {
    id: string;
    function: string;
    tool: string;
    input: string;
    action: string;
    subActionOf?: string;
}

const hotkeys: HotkeyEntry[] = [
    {
        id: 'navigate-scroll',
        function: 'Navigation',
        tool: 'Any tool selected',
        input: 'Mouse wheel OR Arrow keys (↑/↓) OR Page Up/Page Down',
        action: 'Scroll through slices in all 2D views',
    },
    {
        id: 'navigate-pan',
        function: 'Pan Image',
        tool: 'Pan tool selected',
        input: 'Left mouse button + drag',
        action: 'Move image within display area',
    },
    {
        id: 'zoom',
        function: 'Zoom',
        tool: 'Pan tool selected',
        input: 'Ctrl + Mouse wheel',
        action: 'Zoom in/out on image',
    },
    {
        id: 'wl-main',
        function: 'Window/Level Adjustment',
        tool: 'Window/Level tool selected',
        input: 'Left mouse button + drag',
        action: 'Adjust image contrast',
    },
    {
        id: 'wl-vertical',
        function: '',
        tool: '',
        input: 'Vertical mouse movement',
        action: 'Adjust window center (brightness)',
    },
    {
        id: 'wl-horizontal',
        function: '',
        tool: '',
        input: 'Horizontal mouse movement',
        action: 'Adjust window width (contrast)',
    },
    {
        id: 'brush-radius',
        function: 'Brush Tool',
        tool: '2D Brush tool selected',
        input: 'Shift + Left mouse button',
        action: 'Change brush radius',
    },
    {
        id: 'brush-erase',
        function: '',
        tool: '',
        input: 'Alt + Left mouse button',
        action: 'Enable erase mode',
    },
    {
        id: 'structure-management',
        function: 'Structure Management',
        tool: 'Structure selected',
        input: 'Delete key',
        action: 'Remove selected ROI from current slice',
    },
];

function renderInput(input: string): JSX.Element {
    return (
        <Box
            component="span"
            sx={(theme) => ({
                display: 'inline-block',
                fontFamily: 'monospace',
                fontSize: 12,
                backgroundColor: alpha(theme.palette.primary.light, 0.15),
                color: theme.palette.primary.light,
                border: `1px solid ${alpha(theme.palette.primary.light, 0.25)}`,
                borderRadius: '6px',
                px: 0.75,
                whiteSpace: 'pre-wrap',
            })}
        >
            {input}
        </Box>
    );
}

export default function HotkeysHelp() {
    const theme = useTheme();

    return (
        <Box sx={{ maxHeight: 500, overflowY: 'auto', pr: 1 }}>
            <Stack
                spacing={2}
                sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                    borderRadius: 2,
                    p: 2,
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                            variant="subtitle1"
                            fontWeight={600}
                            color="primary.light"
                        >
                            <FormattedMessage id="shortcutsAndControls" />
                        </Typography>
                    </Box>

                    <Divider
                        sx={{
                            borderColor: alpha(theme.palette.primary.main, 0.2),
                        }}
                    />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {hotkeys.map((entry) => (
                        <Grid container spacing={2} key={entry.id}>
                            <Grid item xs={6}>
                                <Typography fontWeight={500} fontSize={14}>
                                    {entry.function}
                                </Typography>
                                {entry.tool && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontStyle: 'italic' }}
                                    >
                                        {entry.tool}
                                    </Typography>
                                )}
                            </Grid>

                            <Grid item xs={6}>
                                {renderInput(entry.input)}
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block' }}
                                >
                                    {entry.action}
                                </Typography>
                            </Grid>
                        </Grid>
                    ))}
                </Box>
            </Stack>
        </Box>
    );
}
